import { useCallback, useEffect, useRef, useState } from 'react'

import { ConfiguracoesVideochamadaModel } from '../model-videochamada'

interface UseUserMediaOptions {
  video: boolean
  audio: boolean
  videoDeviceId?: string
  audioDeviceId?: string
}

interface GetUserMediaOptions {
  requestAudio: boolean
  requestVideo: boolean
  audioDeviceId?: string
  videoDeviceId?: string
}

export const enableTrackStreamEvent = (track?: MediaStreamTrack) => new CustomEvent('enable', { detail: { track } })

const getUserMedia = async ({ requestAudio, requestVideo, audioDeviceId, videoDeviceId }: GetUserMediaOptions) => {
  const audioConstraints = audioDeviceId ? { deviceId: { exact: audioDeviceId } } : true
  const videoConstraints = videoDeviceId ? { deviceId: { exact: videoDeviceId } } : true
  const audio = requestAudio ? audioConstraints : false
  const video = requestVideo ? videoConstraints : false
  return await navigator.mediaDevices?.getUserMedia({ audio, video })
}

export function useUserMedia(options: UseUserMediaOptions) {
  const { audio, audioDeviceId, video, videoDeviceId } = options

  const [stream, setStream] = useState<MediaStream>()
  const [audioDeviceAvailable, setAudioDeviceAvailable] = useState(false)
  const [videoDeviceAvailable, setVideoDeviceAvailable] = useState(false)
  const [mediaDevices, setMediaDevices] = useState<ConfiguracoesVideochamadaModel>()

  const rawStream = useRef<MediaStream>()
  const lastAudioTrackEnabled = useRef<boolean>()
  const lastVideoTrackEnabled = useRef<boolean>()
  const lastAudioDeviceId = useRef<string>()
  const lastVideoDeviceId = useRef<string>()

  const updateAudioTracks = useCallback((currentStream: MediaStream, audioEnabled: boolean) => {
    currentStream.getAudioTracks().forEach((track) => {
      track.enabled = audioEnabled
      currentStream.dispatchEvent(enableTrackStreamEvent(track))
    })
  }, [])

  const updateVideoTracks = useCallback((currentStream: MediaStream, videoEnabled: boolean) => {
    currentStream.getVideoTracks().forEach((track) => {
      track.enabled = videoEnabled
      currentStream.dispatchEvent(enableTrackStreamEvent(track))
    })
  }, [])

  const updateTracks = useCallback(
    (currentStream: MediaStream, audioEnabled: boolean, videoEnabled: boolean) => {
      const audioChanged = lastAudioTrackEnabled.current !== audioEnabled
      const videoChanged = lastVideoTrackEnabled.current !== videoEnabled

      if (audioChanged) updateAudioTracks(currentStream, audioEnabled)
      if (videoChanged) updateVideoTracks(currentStream, videoEnabled)

      lastAudioTrackEnabled.current = audioEnabled
      lastVideoTrackEnabled.current = videoEnabled
    },
    [updateAudioTracks, updateVideoTracks]
  )

  const updateMediaDevices = useCallback((currentStream: MediaStream) => {
    const audioTrack = currentStream.getAudioTracks()[0]
    const videoTrack = currentStream.getVideoTracks()[0]

    const currentAudioDeviceId = audioTrack?.getSettings()?.deviceId
    const audioDeviceLabel = audioTrack?.label

    const currentVideoDeviceId = videoTrack?.getSettings()?.deviceId
    const videoDeviceLabel = videoTrack?.label

    setMediaDevices({
      audioInput: { id: currentAudioDeviceId ?? '', nome: audioDeviceLabel ?? '' },
      audioOutput: { id: 'default', nome: 'Padrão' },
      videoInput: { id: currentVideoDeviceId ?? '', nome: videoDeviceLabel ?? '' },
    })
  }, [])

  const replaceTrack = useCallback(
    (
      currentStream: MediaStream,
      kind: 'audio' | 'video',
      nextTrack: MediaStreamTrack | undefined,
      enabled: boolean
    ) => {
      const currentTracks = kind === 'audio' ? currentStream.getAudioTracks() : currentStream.getVideoTracks()

      currentTracks.forEach((track) => {
        currentStream.removeTrack(track)
        track.stop()
      })

      if (nextTrack) {
        nextTrack.enabled = enabled
        currentStream.addTrack(nextTrack)
        currentStream.dispatchEvent(enableTrackStreamEvent(nextTrack))
      }
    },
    []
  )

  const syncStream = useCallback(
    async ({ audio, audioDeviceId, video, videoDeviceId }: UseUserMediaOptions) => {
      try {
        if (!rawStream.current) {
          const initialStream = await getUserMedia({
            requestAudio: true,
            requestVideo: true,
            audioDeviceId,
            videoDeviceId,
          })

          if (!initialStream) return

          rawStream.current = initialStream
          setStream(initialStream)

          setAudioDeviceAvailable(!!initialStream.getAudioTracks().length)
          setVideoDeviceAvailable(!!initialStream.getVideoTracks().length)

          updateTracks(initialStream, audio, video)
          updateMediaDevices(initialStream)

          lastAudioDeviceId.current = audioDeviceId
          lastVideoDeviceId.current = videoDeviceId
          return
        }

        const currentStream = rawStream.current
        const audioDeviceChanged = lastAudioDeviceId.current !== audioDeviceId
        const videoDeviceChanged = lastVideoDeviceId.current !== videoDeviceId

        if (audioDeviceChanged || videoDeviceChanged) {
          const replacementStream = await getUserMedia({
            requestAudio: audioDeviceChanged,
            requestVideo: videoDeviceChanged,
            audioDeviceId,
            videoDeviceId,
          })

          if (audioDeviceChanged) {
            replaceTrack(currentStream, 'audio', replacementStream?.getAudioTracks()[0], audio)
            lastAudioDeviceId.current = audioDeviceId
          }

          if (videoDeviceChanged) {
            replaceTrack(currentStream, 'video', replacementStream?.getVideoTracks()[0], video)
            lastVideoDeviceId.current = videoDeviceId
          }

          setAudioDeviceAvailable(!!currentStream.getAudioTracks().length)
          setVideoDeviceAvailable(!!currentStream.getVideoTracks().length)
          updateMediaDevices(currentStream)
        }

        updateTracks(currentStream, audio, video)
      } catch (error) {
        if (rawStream.current) {
          setAudioDeviceAvailable(!!rawStream.current.getAudioTracks().length)
          setVideoDeviceAvailable(!!rawStream.current.getVideoTracks().length)
          updateMediaDevices(rawStream.current)
        } else {
          setVideoDeviceAvailable(false)
          setAudioDeviceAvailable(false)
        }
      }
    },
    [replaceTrack, updateMediaDevices, updateTracks]
  )

  useEffect(() => {
    syncStream({ audio, audioDeviceId, video, videoDeviceId })

    const handleDeviceChange = () => syncStream({ audio, audioDeviceId, video, videoDeviceId })
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)

    return () => navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
  }, [syncStream, audio, audioDeviceId, video, videoDeviceId])

  // Para de usar os dispositivos de mídia ao destruir o componente
  useEffect(() => {
    return () => rawStream.current?.getTracks().forEach((track) => track.stop())
  }, [])

  return { stream, audioDeviceAvailable, videoDeviceAvailable, mediaDevices }
}
